// @flow

import { useTranslation } from "react-i18next";
import { SchemaTreeGraph, SchemaTreeGraphEntry } from "./SchemaTreeLogic";
import { TreeViewEditSchemaComponent } from "./TreeViewEditSchemaComponent";
import { TreeViewAddChildSchemaComponent } from "./TreeViewAddSchemaComponent";
import { TreeViewAddGradingServiceComponent } from "./TreeViewAddGradingServiceComponent";
import { useContext, useState } from "react";
import ConfirmButton from "../../../shared/components/ConfirmButton";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../routes/routesconfig";
import { SchemaTree } from "../../../types/SchemaTree";
import Tooltip from "@kea-mod/common/tooltip";
import { ApiContext } from "../../../context/KeaAPI/newApi";
import "./TreeViewRowComponent.css";
import { TreeViewEditGradingServiceComponent } from "./TreeViewEditGradingServiceComponent";

type Props = {
  entry: SchemaTreeGraphEntry;
};

export const TreeViewRowComponent = (props: Props) => {
  const dateOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  } as any;

  const { t, i18n } = useTranslation();
  const { gradingServiceApi, byteFileApi, keyValueApi, schemaApi, schemaTreeApi, textFileApi } = useContext(ApiContext);
  const navigate = useNavigate();

  enum dialogType {
    none,
    addSchema,
    addGradingService,
    editSchema,
    editGradingServiceWeight,
  }

  const [dialog, setDialog] = useState<dialogType>(dialogType.none);

  const toggleDialog = (type: dialogType) => {
    if (dialog === type) setDialog(dialogType.none);
    else setDialog(type);
  };

  // Add a flag variable in the component's state
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // deletion action.

  /**
   * Deletes for each given schema tree entry:
   *  * the given schema tree entry itself
   *  * the direct child of the tree entry if it is a schema, **grading services are not deleted!**
   *  * the keyValueEntries, textFiles and byteFiles of the referenced child
   */
  const deleteStuff = (entriesToDelete: SchemaTreeGraphEntry[]) => {
    // If the function is already running, return immediately
    if (isDeleting) return;

    // Set the flag to true to indicate the function is running
    setIsDeleting(true);

    try {
      // get all keyValueEntries, textFiles and byteFiles of all gradingservices that should be deleted
      // first, get the ids of the tree edges that should be deleted
      const treeEdgesToDelete = entriesToDelete
        .map((entry) => entry.incomingEdge)
        .filter((entry) => entry !== undefined) as SchemaTree[];
      const treeEdgesIDs = new Set(treeEdgesToDelete.map((edge) => edge.id));

      const itemsToDelete = {
        treeEdgesToDelete: treeEdgesToDelete,
        schemasToDelete: entriesToDelete.flatMap((entry) =>
          entry.item === undefined || "topic" in entry.item ? [] : [entry.item],
        ),
        keyValueEntriesToDelete: keyValueApi.elements.filter((kv) => treeEdgesIDs.has(kv.schematreeId)),
        textFilesToDelete: textFileApi.elements.filter((tf) => treeEdgesIDs.has(tf.schematreeId)),
        byteFilesToDelete: byteFileApi.elements.filter((bf) => treeEdgesIDs.has(bf.schematreeId)),
      };

      console.log("Deleting the following items:");
      console.log(itemsToDelete);
      console.log(JSON.stringify(itemsToDelete, null, 2));

      // TODO Fabian: Would be nicer to wait for promises to resolve and add error handling ....
      //            In fact, this task should be done in the backend, and it should be a transaction.
      itemsToDelete.treeEdgesToDelete.forEach((edge) => schemaTreeApi.deleteFunction(edge.id));
      itemsToDelete.schemasToDelete.forEach((schema) => schemaApi.deleteFunction(schema.id));
      itemsToDelete.keyValueEntriesToDelete.forEach((kv) => keyValueApi.deleteFunction(kv.id));
      itemsToDelete.textFilesToDelete.forEach((tf) => textFileApi.deleteFunction(tf.id));
      itemsToDelete.byteFilesToDelete.forEach((bf) => byteFileApi.deleteFunction(bf.id));
    } catch (error) {
      console.error(error);
      console.error(JSON.stringify(error));
      alert("An error occurred while deleting the schema.");
      // TODO Fabian: I strongly dislike that I can't encapsulate the entire deletion in a transaction and an error
      //  in the middle of the deletion causes an undefined state.
      // TODO Fabian: In tests, I could run into inconsistent states!!!! I will have to account for this!!!
      //    because as deletions starts, the state gets updates multiple times, triggering rerenders => easily 100 api calls in a second.!
    } finally {
      // Reset the flag to false to indicate the function has finished running
      setIsDeleting(false);
    }
  };

  const deleteSchemaWithChildren = (entry: SchemaTreeGraphEntry) => {
    // get all child schemas and gradingservices
    const schemaTree = new SchemaTreeGraph(
      [...schemaApi.elements, ...gradingServiceApi.elements],
      schemaTreeApi.elements,
    );
    const successors = entry.item === undefined ? [] : schemaTree.getSuccessors(entry.item.id);

    // restore the incoming edge of the selected schema.
    const toDelete = successors.map((s) => (s.level === 0 ? { ...s, incomingEdge: entry.incomingEdge } : s));
    deleteStuff(toDelete);
  };

  /**
   * Deletes the tree edge that references this grading service.
   * ** Does not delete the grading service itself.**
   * All grading service parameters (keyValues, textFiles, byteFiles) are deleted.
   */
  const deleteGradingService = (graphEntry: SchemaTreeGraphEntry | undefined) => {
    if (!graphEntry) throw Error("This grading service has no tree edge!!");
    if (graphEntry.item === undefined || !("topic" in graphEntry.item))
      throw Error("This is not a grading service!!" + JSON.stringify(graphEntry));
    deleteStuff([graphEntry]);
  };

  // create the green/orange/red circle indicating if the children weight sum to 100 or not
  const createWeightCircle = (entry: SchemaTreeGraphEntry) => {
    if (entry.item === undefined) return <></>;

    const id = entry.item.id;
    const children = schemaTreeApi.elements.filter((edge) => edge.parentId === id);
    const sum = children.length === 0 ? -1 : children.reduce((acc, tree) => acc + tree.weight, 0); // = .sum()

    const circleClass = sum === 100 ? "greenCircle" : sum <= 0 ? "redCircle" : "orangeCircle";

    const tooltip =
      sum === 100
        ? t("schemaTreeTooltipWeight100")
        : sum < 0
        ? t("schemaTreeTooltipNoChildren")
        : t("schemaTreeTooltipWeightNot100").replace("{sum}", sum.toString());

    return (
      <button
        className={`icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top ${circleClass}`}
        data-tooltip={tooltip}
      />
    );
  };

  // create buttons
  const createTableDataItem = (entry: SchemaTreeGraphEntry) => {
    if (entry.item === undefined)
      return (
        <tr className={"schematreeviewrow"}>
          <td
            data-indent-level={entry.level}
            className={"custom-bullet-base custom-bullet" + entry.level}
            style={{
              paddingLeft: `calc(var(--treeview-bullet-left-margin-default) + ${entry.level} * var(--treeview-bullet-left-margin-increase))`,
            }}
          >
            <i>{t("PrivateSchema")}</i>
            <em> ({entry.incomingEdge?.weight ?? 0} %)</em> {createWeightCircle(entry)}
          </td>

          <td></td>
          <td>{t("schematreeviewreadonly")}</td>
        </tr>
      );

    if ("topic" in entry.item) {
      // Item is a grading service
      // TODO: Color for this row??
      return (
        <tr className={"schematreeviewrow"}>
          <td
            data-indent-level={entry.level}
            className={"custom-bullet-base custom-bullet" + entry.level}
            style={{
              paddingLeft: `calc(var(--treeview-bullet-left-margin-default) + ${entry.level} * var(--treeview-bullet-left-margin-increase))`,
            }}
          >
            {t("GradingService")}: {entry.item.name}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={
                (entry.item.description?.trim()?.length ?? -1) > 0 ? entry.item.description : t("treeViewNoDescription")
              }
            ></Tooltip>
            <em>
              {" "}
              (#{entry.incomingEdge?.id ?? "undefined"}) ({entry.incomingEdge?.weight ?? 0} %)
            </em>
          </td>

          <td>{new Date(entry.item.updatedAt).toLocaleDateString(i18n.language, dateOptions)}</td>
          <td>
            {!entry.incomingEdge || entry.incomingEdge.writable ? (
              <>
                <button
                  className="button is-primary"
                  onClick={() =>
                    navigate(
                      routes.editgradingserviceparameters.replace(":id", entry.incomingEdge?.id?.toString() ?? ""),
                    )
                  }
                >
                  {t("treeViewEditGradingServiceParameter")}
                </button>

                <button className="button" onClick={() => toggleDialog(dialogType.editGradingServiceWeight)}>
                  {t("treeViewEditGradingServiceWeight")}
                </button>

                <ConfirmButton
                  id={entry.item.id}
                  name={entry.item.name}
                  proceedAction={() => deleteGradingService(entry)}
                  question={`deleteConfirmationGradingService`}
                  explanation={`deleteConfirmationGradingServiceIrreversible`}
                  color="is-danger"
                  buttonLabel={`delete`}
                />
              </>
            ) : (
              <span>{t("schematreeviewreadonly")}</span>
            )}
          </td>
        </tr>
      );
    } else if (entry.level <= 0) {
      return (
        // Item is a schema and a root
        <tr className={"schematreeviewrow"}>
          <th
            data-indent-level={entry.level}
            className={"custom-bullet-base custom-bullet" + entry.level}
            style={{
              paddingLeft: `calc(var(--treeview-bullet-left-margin-default) + ${entry.level} * var(--treeview-bullet-left-margin-increase))`,
            }}
          >
            {t("SchemaRoot")}: {entry.item.name}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={
                (entry.item.description?.trim()?.length ?? -1) > 0 ? entry.item.description : t("treeViewNoDescription")
              }
            ></Tooltip>
            {createWeightCircle(entry)}{" "}
          </th>
          <th>{new Date(entry.item.updatedAt).toLocaleDateString(i18n.language, dateOptions)}</th>
          <td>
            {entry.item.writable && (!entry.incomingEdge || entry.incomingEdge.writable) ? (
              <>
                {/*// Edit Inline?*/}
                <button className="button is-primary" onClick={() => toggleDialog(dialogType.editSchema)}>
                  {t("treeViewEditSchema")}
                </button>

                {/*// Add Schema*/}
                <button className="button" onClick={() => toggleDialog(dialogType.addSchema)}>
                  {t("treeViewAddSchema")}
                </button>

                {/*// Add GradingService*/}
                <button className="button" onClick={() => toggleDialog(dialogType.addGradingService)}>
                  {t("treeViewAddGradingService")}
                </button>

                {/*// Delete this schema and children*/}
                <ConfirmButton
                  id={entry.item.id}
                  name={entry.item.name}
                  proceedAction={() => deleteSchemaWithChildren(entry)}
                  question={`treeViewDeleteConfirmationSchemaAndChildren`}
                  explanation={`irreversibleStep`}
                  color="is-danger"
                  buttonLabel={`delete`}
                />
              </>
            ) : (
              <span>{t("schematreeviewreadonly")}</span>
            )}
          </td>
        </tr>
      );
    } else {
      // Item is a schema and a child
      return (
        <tr className={"schematreeviewrow"}>
          <td
            data-indent-level={entry.level}
            className={"custom-bullet-base custom-bullet" + entry.level}
            style={{
              paddingLeft: `calc(var(--treeview-bullet-left-margin-default) + ${entry.level} * var(--treeview-bullet-left-margin-increase))`,
            }}
          >
            <strong>
              {t("Schema")}: {entry.item.name}
            </strong>
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={
                (entry.item.description?.trim()?.length ?? -1) > 0 ? entry.item.description : t("treeViewNoDescription")
              }
            ></Tooltip>
            <em> ({entry.incomingEdge?.weight ?? 0} %)</em> {createWeightCircle(entry)}
          </td>

          <td>{new Date(entry.item.updatedAt).toLocaleDateString(i18n.language, dateOptions)}</td>
          <td>
            {entry.item.writable && (!entry.incomingEdge || entry.incomingEdge.writable) ? (
              <>
                {/*// Edit Inline?*/}
                <button className="button is-primary" onClick={() => toggleDialog(dialogType.editSchema)}>
                  {t("treeViewEditSchema")}
                </button>

                {/*// Add Schema*/}
                <button className="button" onClick={() => toggleDialog(dialogType.addSchema)}>
                  {t("treeViewAddSchema")}
                </button>

                {/*// Add GradingService*/}
                <button className="button" onClick={() => toggleDialog(dialogType.addGradingService)}>
                  {t("treeViewAddGradingService")}
                </button>

                {/*// Delete this schema and children*/}
                <ConfirmButton
                  id={entry.item.id}
                  name={entry.item.name}
                  proceedAction={() => deleteSchemaWithChildren(entry)}
                  question={`treeViewDeleteConfirmationSchemaAndChildren`}
                  explanation={`irreversibleStep`}
                  color="is-danger"
                  buttonLabel={`delete`}
                />
              </>
            ) : (
              <span>{t("schematreeviewreadonly")}</span>
            )}
          </td>
        </tr>
      );
    }
  };

  const createTableInlineForms = (entry: SchemaTreeGraphEntry) => {
    if (entry.item === undefined) return <></>;

    if ("topic" in entry.item) {
      return (
        <TreeViewEditGradingServiceComponent
          gradingService={entry.item}
          schemaTree={entry.incomingEdge}
          closeAction={() => setDialog(dialogType.none)}
          visible={dialog === dialogType.editGradingServiceWeight}
        />
      );
    }

    // not writeable?
    if (!entry.item.writable || (entry.incomingEdge && !entry.incomingEdge.writable)) return <></>;

    return (
      <tr key={"sttr" + (entry.incomingEdge?.id ?? "root").toString()} style={{ padding: 0 }}>
        <td style={{ padding: 0 }}>
          <TreeViewEditSchemaComponent
            schemaId={entry.item.id}
            closeAction={() => setDialog(dialogType.none)}
            visible={dialog === dialogType.editSchema}
            schema={entry.item}
            schemaTree={entry.incomingEdge}
          />
          <TreeViewAddChildSchemaComponent
            parentSchemaId={entry.item.id}
            closeAction={() => setDialog(dialogType.none)}
            visible={dialog === dialogType.addSchema}
          />
          <TreeViewAddGradingServiceComponent
            parentSchemaId={entry.item.id}
            closeAction={() => setDialog(dialogType.none)}
            visible={dialog === dialogType.addGradingService}
          />
        </td>
      </tr>
    );
  };

  return (
    <>
      {createTableDataItem(props.entry)}
      {createTableInlineForms(props.entry)}
    </>
  );
};
